import {
	BrowserClient,
	defaultStackParser,
	getDefaultIntegrations,
	makeFetchTransport,
	Scope,
	type Event as SentryEvent,
} from '@sentry/browser';

import { BFFError, BFFErrorEnum, BFFNetworkError } from '../../common/errors';
import { getEnvironment } from '../environment';
import {
	classifyHttpStatus,
	DEFAULT_SENTRY_CONFIG,
	extractHttpStatusFromMessage,
	type HttpStatusClassification,
	RATE_LIMITED_SAMPLE_RATE,
	SENTRY_DSN,
	SUPPRESSED_BACKEND_ERROR_PATTERNS,
} from './constants';
import type { SentryClientConfig, SentryEventTags } from './types';

let sentryClient: BrowserClient | undefined;
let activeSuppressBackendErrors: boolean | undefined;
let scope: Scope = new Scope();

type ErrorLike = { message?: string; name?: string };

/** Reads `name` / `message` off an Error or a normalised plain-object error, if present. */
function toErrorLike(value: unknown): ErrorLike {
	if (typeof value !== 'object' || value === null) {
		return {};
	}
	const { name, message } = value as Record<string, unknown>;
	return {
		name: typeof name === 'string' ? name : undefined,
		message: typeof message === 'string' ? message : undefined,
	};
}

function isBFFErrorName(name: unknown): boolean {
	return name === BFFErrorEnum.BFF_ERROR || name === BFFErrorEnum.BFF_NETWORK_ERROR;
}

/** Parses a BFF `status` extra into an HTTP status code. Non-status values (e.g. "error") are ignored. */
function parseHttpStatus(status: unknown): number | undefined {
	if (typeof status === 'number') {
		return Number.isInteger(status) ? status : undefined;
	}
	if (typeof status === 'string' && /^\d{3}$/.test(status.trim())) {
		return Number(status);
	}
	return undefined;
}

/** Joins `type` and `value` the same way Sentry's `ignoreErrors` filter does. */
function withType(type: string | undefined, value: string | undefined): string | undefined {
	return type && value ? `${type}: ${value}` : undefined;
}

/**
 * Classifies a Sentry event as non-actionable BE / network noise:
 * - `suppress`: transport-level failure, or an HTTP status that isn't actionable in the FE.
 * - `sample`: rate-limited (429); only a fraction should be reported.
 * - `report`: everything else, including FE-caused 4xx responses and genuine FE bugs.
 *
 * BFF wrappers (BFF_ERROR / BFF_NETWORK_ERROR) have a generic message (e.g. "Failed to fetch
 * universal create menu items"), so the wrapped `originalError` and `status` extras are inspected too.
 *
 * @internal exported for unit tests; do not import outside `services/sentry`.
 */
export function classifyBackendError(event: SentryEvent): HttpStatusClassification {
	const extras: Record<string, unknown> = event.extra ?? {};
	// Sentry chains causes as multiple `values`. Older events have a single value, newer linked
	// errors can have several — classify as suppress if any of them is a known transport failure.
	const exceptions = event.exception?.values ?? [];
	const original = toErrorLike(extras.originalError);

	const candidates = [
		event.message,
		...exceptions.flatMap((e) => [e.value, withType(e.type, e.value)]),
		original.message,
		withType(original.name, original.message),
	].filter((candidate): candidate is string => Boolean(candidate));

	if (SUPPRESSED_BACKEND_ERROR_PATTERNS.some((re) => candidates.some((c) => re.test(c)))) {
		return 'suppress';
	}

	// Prefer the explicit status on BFF wrappers; fall back to the `fetchJSON` message format on
	// the wrapped original error. The outer exception value and `event.message` are usually
	// derived from the same `fetchJSON` message for status-bearing errors, so these last two
	// parses are defensive fallbacks for non-wrapped errors or future error shapes.
	const httpStatus =
		(isBFFErrorName(extras.name) ? parseHttpStatus(extras.status) : undefined) ??
		extractHttpStatusFromMessage(original.message) ??
		extractHttpStatusFromMessage(exceptions[exceptions.length - 1]?.value) ??
		extractHttpStatusFromMessage(event.message);

	return classifyHttpStatus(httpStatus);
}

/**
 * Returns true for known BE / network errors that should never be reported.
 * @internal exported for unit tests; do not import outside `services/sentry`.
 */
export function isSuppressedBackendError(event: SentryEvent): boolean {
	return classifyBackendError(event) === 'suppress';
}

/**
 * Applies BE-noise suppression to an event: drops suppressed events, keeps a tagged
 * `RATE_LIMITED_SAMPLE_RATE` sample of rate-limited events, and passes everything else through.
 * @internal exported for unit tests; do not import outside `services/sentry`.
 */
export function applyBackendErrorSuppression(
	event: SentryEvent,
	random: () => number = Math.random,
): SentryEvent | null {
	switch (classifyBackendError(event)) {
		case 'suppress':
			return null;
		case 'sample':
			if (random() >= RATE_LIMITED_SAMPLE_RATE) {
				return null;
			}
			return {
				...event,
				tags: { ...event.tags, suppressionSampleRate: String(RATE_LIMITED_SAMPLE_RATE) },
			};
		default:
			return event;
	}
}

export function logException(exception: Error | unknown, tags: SentryEventTags): void {
	scope.setTags(tags);

	if (exception instanceof BFFError) {
		const error = exception as BFFError;

		scope.setExtras({
			name: error.name,
			context: error.context,
			status: error.status,
			timestamp: error.timestamp,
			originalError: error.originalError,
			...(error instanceof BFFNetworkError && { url: error.url }),
		});
	}

	sentryClient?.captureException(exception, undefined, scope);

	// Unset tags to prevent an instance of the error boundary from logging with a specific tag,
	// and then another instance from wrongly passing it despite not having the tag.
	for (const [key, _] of Object.entries(tags)) {
		scope.setTag(key, undefined);
	}

	// Unset any extras to prevent them from persisting
	scope.setExtras({});
}

export function setupSentryClient({
	sentryConfig,
}: {
	sentryConfig?: SentryClientConfig;
}): boolean {
	const { dsn = SENTRY_DSN, env, suppressBackendErrors = true } = sentryConfig || {};

	if (sentryClient) {
		// Singleton: later callers can't change the suppression behaviour, so warn once if they
		// try. Products should coordinate on this setting at the boundary that initialises first.
		if (
			activeSuppressBackendErrors !== undefined &&
			activeSuppressBackendErrors !== suppressBackendErrors
		) {
			// eslint-disable-next-line no-console
			console.warn(
				`[navigation-error-boundary] setupSentryClient was already initialised with suppressBackendErrors=${activeSuppressBackendErrors}; later value ${suppressBackendErrors} is ignored.`,
			);
		}
		return true;
	}

	// SSR-safe check. If we are in SSR, we don't need a sentryClient
	if (typeof window === 'undefined') {
		return false;
	}

	activeSuppressBackendErrors = suppressBackendErrors;
	const environment = getEnvironment(env);

	// filter integrations that use the global variable
	const integrations = getDefaultIntegrations({}).filter((defaultIntegration) => {
		return !['BrowserApiErrors', 'Breadcrumbs', 'GlobalHandlers'].includes(defaultIntegration.name);
	});

	sentryClient = new BrowserClient({
		...DEFAULT_SENTRY_CONFIG,
		ignoreErrors: suppressBackendErrors
			? [...DEFAULT_SENTRY_CONFIG.ignoreErrors, ...SUPPRESSED_BACKEND_ERROR_PATTERNS]
			: DEFAULT_SENTRY_CONFIG.ignoreErrors,
		dsn,
		environment,
		beforeSend: (event) => {
			// Only events captured via logException (which sets teamName)
			if (event.tags?.teamName === undefined) {
				return null;
			}
			return suppressBackendErrors ? applyBackendErrorSuppression(event) : event;
		},
		attachStacktrace: true,
		transport: makeFetchTransport,
		stackParser: defaultStackParser,
		integrations,
	});

	scope.setClient(sentryClient);
	sentryClient.init();
	return true;
}

/** @internal Reset the Sentry client singleton. Test-only. */
export function __resetSentryClient(): void {
	sentryClient = undefined;
	activeSuppressBackendErrors = undefined;
	scope = new Scope();
}
